/** Student family detail tab. */
import * as React from 'react';
import { type LucideIcon, Users, User, BarChart3, TrendingUp, Calendar } from 'lucide-react';
import { LabelBadge } from '@/components/common/LabelBadge';
import { DetailsCard } from '@/components/common/DetailsCard';
import { DetailField } from '@/components/common/details/DetailField';
import { useQuery } from '@tanstack/react-query';
import type { StudentRecord } from '@/types/remote/student.types';
import {
  completionBarColor,
  formatDate,
  daysSince,
  formatRegistrationManagerName,
} from '@/components/students/remote/student-table-utils';
import { getUserHistory } from '@/services/history.service';
import { getRoleLabel } from '@/utils/role-label-utils';
import { rankedContactDetailFields } from '@/utils/user-contact-utils';

/** Props for {@link StudentFamilyTab}. */
interface StudentFamilyTabProps {
  detailRecord: StudentRecord;
  isAdultStudent: boolean;
  statusConfig:
    | { label: string; icon: LucideIcon; badgeVariant: any; badgeColor?: string }
    | undefined;
  situationConfig: { label: string; icon: LucideIcon; badgeVariant: any; badgeColor?: string };
}

/** Student family detail tab. */
export const StudentFamilyTab: React.FC<StudentFamilyTabProps> = ({
  detailRecord,
  isAdultStudent,
  statusConfig,
  situationConfig,
}) => {
  const isArchived = detailRecord.user?.status === 'archived';
  const { data: history } = useQuery({
    queryKey: ['history', 'user', detailRecord.userId],
    queryFn: () => getUserHistory(detailRecord.userId),
    enabled: isArchived && !!detailRecord.userId,
  });
  const archive = history?.archive;
  const archiveReason =
    archive?.comment && archive.reason
      ? `${archive.reason} — ${archive.comment}`
      : (archive?.reason ?? archive?.comment ?? '—');

  return (
    <div className="details-view-grid mt-6">
      <DetailsCard icon={Users} title="Informations de base" colorName="color-2" colSpan={1}>
        <div className="space-y-3">
          {detailRecord.linkedUser ? (
            <>
              <DetailField
                label="Parent lié"
                value={`${detailRecord.linkedUser.firstName} ${detailRecord.linkedUser.lastName}`}
              />
              {rankedContactDetailFields(
                detailRecord.linkedUser.primaryContactKind,
                detailRecord.linkedUser.email,
                detailRecord.linkedUser.phone,
              ).map((field) => (
                <DetailField
                  key={`parent-${field.label}`}
                  label={`${field.label} parent`}
                  value={field.value}
                />
              ))}
            </>
          ) : (
            <p className="text-sm text-muted-foreground">Aucun parent lié.</p>
          )}
          {isAdultStudent && (
            <div className="border-t pt-3 mt-3">
              <p className="text-xs text-muted-foreground mb-2 font-medium">
                Contact propre (majeur)
              </p>
              {rankedContactDetailFields(
                detailRecord.user?.primaryContactKind,
                detailRecord.user?.email,
                detailRecord.user?.phone,
              ).map((field) => (
                <DetailField
                  key={`student-${field.label}`}
                  label={`${field.label} élève`}
                  value={field.value}
                />
              ))}
            </div>
          )}
        </div>
      </DetailsCard>

      <DetailsCard icon={BarChart3} title="Métriques" colorName="color-1" colSpan={1}>
        <div className="space-y-3">
          <DetailField
            label="Statut"
            value={
              statusConfig ? (
                <LabelBadge
                  label={statusConfig.label}
                  icon={statusConfig.icon}
                  variant={statusConfig.badgeVariant}
                  color={statusConfig.badgeColor}
                  size="sm"
                />
              ) : (
                detailRecord.onboardingStatus
              )
            }
          />
          <DetailField
            label="Situation"
            value={
              <LabelBadge
                label={situationConfig.label}
                icon={situationConfig.icon}
                variant={situationConfig.badgeVariant}
                color={situationConfig.badgeColor}
                size="sm"
              />
            }
          />
          <DetailField
            label={getRoleLabel('coordinator')}
            value={
              formatRegistrationManagerName(detailRecord) === '—' ? (
                '—'
              ) : (
                <LabelBadge
                  label={formatRegistrationManagerName(detailRecord)}
                  icon={User}
                  variant="outlined"
                  color="rgb(var(--color-1-nuance-1))"
                  size="sm"
                />
              )
            }
          />
          {isArchived && archive ? (
            <>
              <DetailField label="Archivé par" value={archive.archivedBy?.userFullName ?? '—'} />
              <DetailField label="Motif" value={archiveReason} />
            </>
          ) : null}
        </div>
      </DetailsCard>

      <DetailsCard icon={TrendingUp} title="Progression" colorName="color-3" colSpan={2}>
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">
              {detailRecord.completion.completedSections}/{detailRecord.completion.totalSections}{' '}
              sections complétées
            </span>
            <span className="text-sm text-muted-foreground">
              {detailRecord.completion.completedSectionsPercentage}%
            </span>
          </div>
          <div className="w-full h-3 bg-[var(--color-1-nuance-3)] rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all"
              style={{
                width: `${detailRecord.completion.completedSectionsPercentage}%`,
                backgroundColor: completionBarColor(
                  detailRecord.completion.completedSectionsPercentage,
                ),
              }}
            />
          </div>
        </div>
      </DetailsCard>

      <DetailsCard icon={Calendar} title="Dates" colorName="color-2" colSpan={1}>
        <div className="space-y-3">
          <DetailField label="Inscrit le" value={formatDate(detailRecord.createdAt)} />
          <DetailField label="Ancienneté" value={daysSince(detailRecord.createdAt)} />
          <DetailField label="Dernière mise à jour" value={formatDate(detailRecord.updatedAt)} />
          {isArchived && archive ? (
            <DetailField label="Date d'archivage" value={formatDate(archive.archivedAt)} />
          ) : null}
        </div>
      </DetailsCard>
    </div>
  );
};
